"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuthenticatedUser } from "@/app/hooks/use-authenticated-user";
import { buildAppUrl, type AppConfig } from "../config/get-config";

type HomeAuthCtaProps = {
  runtimeConfig: AppConfig;
  primaryLabel: string;
  primaryAriaLabel: string;
  primaryClassName?: string;
  secondaryLabel?: string;
  secondaryAriaLabel?: string;
  secondaryClassName?: string;
  authenticatedLabel?: string;
  authenticatedAriaLabel?: string;
  containerClassName?: string;
  showArrowOnPrimary?: boolean;
  showArrowOnAuthenticated?: boolean;
};

export default function HomeAuthCta({
  runtimeConfig,
  primaryLabel,
  primaryAriaLabel,
  primaryClassName,
  secondaryLabel,
  secondaryAriaLabel,
  secondaryClassName,
  authenticatedLabel = "Start Practicing",
  authenticatedAriaLabel,
  containerClassName,
  showArrowOnPrimary = false,
  showArrowOnAuthenticated = false,
}: HomeAuthCtaProps) {
  const isAuthenticated = useAuthenticatedUser();
  const continueHref = React.useMemo(() => buildAppUrl("/", runtimeConfig), [runtimeConfig]);
  const signupHref = React.useMemo(() => buildAppUrl("/signup", runtimeConfig), [runtimeConfig]);
  const loginHref = React.useMemo(() => buildAppUrl("/login", runtimeConfig), [runtimeConfig]);

  if (isAuthenticated) {
    return (
      <div className={containerClassName}>
        <Button asChild className={primaryClassName}>
          <a href={continueHref} aria-label={authenticatedAriaLabel || authenticatedLabel}>
            {authenticatedLabel}
            {showArrowOnAuthenticated ? (
              showArrowOnPrimary ? (
                <span className="ml-4 grid size-11 shrink-0 place-items-center rounded-full bg-white text-secondary shadow-[0_8px_22px_rgba(255,255,255,0.22)]">
                  <ArrowRight className="size-5" />
                </span>
              ) : (
                <ArrowRight className="ml-2 size-4" />
              )
            ) : null}
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <Button asChild className={primaryClassName}>
        <a href={signupHref} aria-label={primaryAriaLabel}>
          {primaryLabel}
          {showArrowOnPrimary ? (
            <span className="ml-4 grid size-11 shrink-0 place-items-center rounded-full bg-white text-secondary shadow-[0_8px_22px_rgba(255,255,255,0.22)]">
              <ArrowRight className="size-5" />
            </span>
          ) : null}
        </a>
      </Button>
      {secondaryLabel && secondaryAriaLabel && secondaryClassName ? (
        <Button asChild variant="outline" className={secondaryClassName}>
          <a href={loginHref} aria-label={secondaryAriaLabel}>
            {secondaryLabel}
          </a>
        </Button>
      ) : null}
    </div>
  );
}